import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, UserRound, Building2, CheckCircle2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { submitJobSeeker, submitCompany } from "@/lib/site";

const inputCls =
    "bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F47B20] focus-visible:border-[#F47B20] h-11";

const Field = ({ label, required, children }) => (
    <div className="space-y-1.5">
        <Label className="text-slate-300 text-sm">
            {label} {required && <span className="text-[#F47B20]">*</span>}
        </Label>
        {children}
    </div>
);

const educationOptions = [
    "ITI",
    "Diploma",
    "Graduate",
    "Post Graduate",
    "10th Pass",
    "12th Pass",
    "No Formal Education",
];

const candidateTypes = [
    "ITI Graduate",
    "Diploma Students",
    "Graduate / Post Graduate",
    "10th / 12th Pass",
    "Non-Educated / Freshers",
    "Mixed / Multiple",
];

function JobSeekerForm() {
    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        education: "",
        trade: "",
        experience: "",
        preferred_location: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.phone || !form.email || !form.education) {
            toast.error("Please fill in name, phone, email and education level.");
            return;
        }
        if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        try {
            await submitJobSeeker(form);
            setDone(true);
            toast.success("Enquiry submitted! Our team will contact you soon.");
            setForm({ full_name: "", phone: "", email: "", education: "", trade: "", experience: "", preferred_location: "", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again or call us directly.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid="job-seeker-success" className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 size={56} className="text-[#F47B20] mb-4" />
                <h3 className="font-display text-2xl font-bold text-white">Thank you!</h3>
                <p className="mt-2 text-slate-400 max-w-sm">
                    Your job enquiry has been received. Our placement team will reach out to you shortly.
                </p>
                <button onClick={() => setDone(false)} data-testid="job-seeker-reset" className="mt-6 text-sm text-[#F47B20] underline underline-offset-4">
                    Submit another enquiry
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="job-seeker-form" className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name" required>
                <Input data-testid="js-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
            </Field>
            <Field label="Phone Number" required>
                <Input data-testid="js-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
            </Field>
            <Field label="Email" required>
                <Input data-testid="js-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </Field>
            <Field label="Education Level" required>
                <Select value={form.education} onValueChange={set("education")}>
                    <SelectTrigger data-testid="js-education" className={inputCls}>
                        <SelectValue placeholder="Select education" />
                    </SelectTrigger>
                    <SelectContent>
                        {educationOptions.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Trade / Desired Role">
                <Input data-testid="js-trade" className={inputCls} value={form.trade} onChange={set("trade")} placeholder="e.g. Fitter, Electrician, Office" />
            </Field>
            <Field label="Experience">
                <Input data-testid="js-experience" className={inputCls} value={form.experience} onChange={set("experience")} placeholder="e.g. Fresher / 2 years" />
            </Field>
            <div className="sm:col-span-2">
                <Field label="Preferred Location">
                    <Input data-testid="js-location" className={inputCls} value={form.preferred_location} onChange={set("preferred_location")} placeholder="e.g. Pune, Pimpri Chinchwad" />
                </Field>
            </div>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <Textarea data-testid="js-message" className={`${inputCls} h-24`} value={form.message} onChange={set("message")} placeholder="Tell us about the job you are looking for" />
                </Field>
            </div>
            <button type="submit" disabled={loading} data-testid="job-seeker-submit" className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-[#F47B20] px-7 py-3.5 font-semibold text-white orange-glow hover:bg-[#e06a10] disabled:opacity-60 transition-all">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? "Submitting..." : "Submit Job Enquiry"}
            </button>
        </form>
    );
}

function CompanyForm() {
    const [form, setForm] = useState({
        company_name: "",
        contact_person: "",
        phone: "",
        email: "",
        positions: "",
        candidate_type: "",
        vacancies: "",
        location: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name || !form.contact_person || !form.phone || !form.email || !form.positions || !form.candidate_type) {
            toast.error("Please fill in all required fields.");
            return;
        }
        if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        try {
            await submitCompany(form);
            setDone(true);
            toast.success("Hiring request submitted! Our team will reach out soon.");
            setForm({ company_name: "", contact_person: "", phone: "", email: "", positions: "", candidate_type: "", vacancies: "", location: "", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again or call us directly.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid="company-success" className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 size={56} className="text-[#F47B20] mb-4" />
                <h3 className="font-display text-2xl font-bold text-white">Request received!</h3>
                <p className="mt-2 text-slate-400 max-w-sm">
                    Thank you. Our team will connect with you to understand your hiring needs and share suitable candidates.
                </p>
                <button onClick={() => setDone(false)} data-testid="company-reset" className="mt-6 text-sm text-[#F47B20] underline underline-offset-4">
                    Submit another request
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="company-form" className="grid sm:grid-cols-2 gap-5">
            <Field label="Company Name" required>
                <Input data-testid="co-name" className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="Company / Organization" />
            </Field>
            <Field label="Contact Person" required>
                <Input data-testid="co-contact" className={inputCls} value={form.contact_person} onChange={set("contact_person")} placeholder="Your name" />
            </Field>
            <Field label="Phone Number" required>
                <Input data-testid="co-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
            </Field>
            <Field label="Email" required>
                <Input data-testid="co-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="hr@company.com" />
            </Field>
            <Field label="Positions Needed" required>
                <Input data-testid="co-positions" className={inputCls} value={form.positions} onChange={set("positions")} placeholder="e.g. Machine Operators, Guards" />
            </Field>
            <Field label="Candidate Type" required>
                <Select value={form.candidate_type} onValueChange={set("candidate_type")}>
                    <SelectTrigger data-testid="co-candidate-type" className={inputCls}>
                        <SelectValue placeholder="Select candidate type" />
                    </SelectTrigger>
                    <SelectContent>
                        {candidateTypes.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Number of Vacancies">
                <Input data-testid="co-vacancies" className={inputCls} value={form.vacancies} onChange={set("vacancies")} placeholder="e.g. 10" />
            </Field>
            <Field label="Location">
                <Input data-testid="co-location" className={inputCls} value={form.location} onChange={set("location")} placeholder="Work location" />
            </Field>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <Textarea data-testid="co-message" className={`${inputCls} h-24`} value={form.message} onChange={set("message")} placeholder="Describe your hiring requirement" />
                </Field>
            </div>
            <button type="submit" disabled={loading} data-testid="company-submit" className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-[#F47B20] px-7 py-3.5 font-semibold text-white orange-glow hover:bg-[#e06a10] disabled:opacity-60 transition-all">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? "Submitting..." : "Submit Hiring Request"}
            </button>
        </form>
    );
}

export const EnquirySection = () => {
    return (
        <section id="enquiry" data-testid="enquiry-section" className="relative py-28 sm:py-32 px-5 sm:px-8">
            <div className="absolute inset-0 radial-glow opacity-60" />
            <div className="relative mx-auto max-w-6xl grid lg:grid-cols-[0.85fr_1.4fr] gap-14 items-start">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="lg:sticky lg:top-32"
                >
                    <span className="text-xs tracking-[0.35em] text-[#F47B20] uppercase font-semibold">
                        Get Started
                    </span>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight">
                        Tell us what you need
                    </h2>
                    <p className="mt-5 text-slate-400 leading-relaxed">
                        Looking for a job or looking to hire? Fill the form and our team
                        will reach out. Job seeker enquiries go to our placement desk,
                        employer requests go to our hiring desk.
                    </p>
                    <div className="mt-8 space-y-3">
                        <div className="flex items-center gap-3 text-slate-300 text-sm">
                            <UserRound size={18} className="text-[#F47B20]" /> Free registration for job seekers
                        </div>
                        <div className="flex items-center gap-3 text-slate-300 text-sm">
                            <Building2 size={18} className="text-[#F47B20]" /> Pre-screened talent for employers
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="glass rounded-3xl p-6 sm:p-9"
                >
                    <Tabs defaultValue="seeker">
                        <TabsList className="grid grid-cols-2 w-full h-auto bg-white/5 p-1.5 rounded-full mb-8">
                            <TabsTrigger data-testid="tab-job-seeker" value="seeker" className="rounded-full py-3 data-[state=active]:bg-[#F47B20] data-[state=active]:text-white text-slate-300 font-semibold">
                                I Need a Job
                            </TabsTrigger>
                            <TabsTrigger data-testid="tab-employer" value="company" className="rounded-full py-3 data-[state=active]:bg-[#1E5AA8] data-[state=active]:text-white text-slate-300 font-semibold">
                                I Need Employees
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="seeker">
                            <JobSeekerForm />
                        </TabsContent>
                        <TabsContent value="company">
                            <CompanyForm />
                        </TabsContent>
                    </Tabs>
                </motion.div>
            </div>
        </section>
    );
};
